import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { ForgotPasswordApiRoaming } from '@api-roaming/models/forgot-password-api-roaming';
import { ForgotPasswordEmailApiRoaming } from '@api-roaming/models/forgot-password-email-api-roaming';
import { PasswordDataApiRoaming } from '@api-roaming/models/password-data-api-roaming';
import { ErrorResponse } from '@api/models';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RouteEntity } from '@core/router/router.entity';
import { UserState } from '@core/state/user.state';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '@root/material';
import { RxEffects } from '@rx-angular/state/effects';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { RESEND_CODE_CONFIRM_TIMEOUT } from '@shared/constants';
import { LockupTimePipe } from '@shared/pipes';
import { IMaskDirective } from 'angular-imask';
import { Observable, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';


/**
 * Password recovery confirmation page
 */
@Component({
    selector: 'app-recover-confirm',
    templateUrl: './recover-confirm.component.html',
    styleUrls: ['./recover-confirm.component.scss'],
    standalone: true,
    imports: [
        MatCardModule,
        TranslateModule,
        AsyncPipe,
        FormErrorComponent,
        ReactiveFormsModule,
        MatFormFieldModule,
        IMaskDirective,
        MatInputModule,
        MaterialModule,
        LockupTimePipe,
        MatExpansionModule,
        MatButtonModule,
    ],
    providers: [RxEffects],
})
export class RecoverConfirmComponent {
    readonly #userState: UserState = inject(UserState);
    readonly #effects: RxEffects = inject(RxEffects);
    readonly #router: Router = inject(Router);

    public readonly pending$: Observable<boolean> = this.#userState.loading$.pipe(
        map((loading: Record<StartWithUnit, boolean>) => loading?.[StartWithUnit.RecoverConfirmPassword]),
    );

    public readonly error$: Observable<ErrorResponse> = this.#userState.errors$.pipe(
        map((errors: Record<StartWithUnit, ErrorResponse>) => errors?.[StartWithUnit.RecoverConfirmPassword]),
    );

    public readonly loggedIn$: Observable<boolean> = this.#userState.isAuthorized$;
    public readonly recoverData$: Observable<ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming> =
        this.#userState.recoverPasswordData$;

    public readonly form: UntypedFormGroup = new UntypedFormGroup({
        code: new UntypedFormControl('', [Validators.required]),
    });

    public lockUp$: Observable<number> | null;

    readonly #resendLockup: number = RESEND_CODE_CONFIRM_TIMEOUT;

    constructor() {
        this.#effects.register(
            this.recoverData$.pipe(filter((recoverData: ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming) => !recoverData)),
            () => this.#router.navigate([RouteEntity.Root, RouteEntity.Recover]),
        );

        this.lockupResend();
    }

    public get codeControl(): AbstractControl {
        return this.form ? this.form.get('code') : undefined;
    }

    public onSubmit(): void {
        const data: PasswordDataApiRoaming = {
            smsCode: this.codeControl.value,
        };

        this.#effects.register(
            this.recoverData$,
            (rData: ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming) => {
                if (rData.sysCode === 'email') {
                    this.#userState.recoverConfirmByEmail(data);
                } else {
                    this.#userState.recoverConfirmByPhone(data);
                }
            },
        );
    }

    public onResendClick(): void {
        this.codeControl.setValue('');
        this.form.markAsUntouched();
        this.#effects.register(
            this.recoverData$,
            (rData: ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming) => {
                if (rData.sysCode === 'email') {
                    this.#userState.recoverByEmail(rData);
                } else {
                    this.#userState.recoverByPhone(rData);
                }
            },
        );

        this.lockupResend();
    }

    private lockupResend(): void {
        this.lockUp$ = timer(0, 1000).pipe(
            map((i: number) => this.#resendLockup - i),
            take(this.#resendLockup + 1),
        );
    }
}
